import React, { useEffect, useMemo, useState } from "react";
import routeLogs from "./routes";
import { useDispatch, useSelector } from "react-redux";
import { routeAuth } from "../AuthPage";
import { Redirect } from "react-router-dom";
import { getRequest } from "../../services/requestController";
import { setLoading } from "../../redux/actions";
import dayjs from "dayjs";
import ACTION_LABELS, { getActionLabel } from "../../utils/actionLabels";
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    DatePicker,
    Descriptions,
    Divider,
    Empty,
    Input,
    Modal,
    Progress,
    Row,
    Segmented,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    notification,
} from "antd";
import {
    BarChartOutlined,
    CalendarOutlined,
    FilterOutlined,
    ReloadOutlined,
    SearchOutlined,
    ThunderboltOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { UserHeader } from "../../components/UserHeader/UserHeader";
import "./styles.scss";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const safeStr = (v) => (typeof v === "string" ? v : v === null || v === undefined ? "" : String(v));

const formatTs = (v) => {
    const d = dayjs(v);
    if (!d.isValid()) return "—";
    return d.format("DD.MM.YYYY HH:mm:ss");
};

const toJsonPreview = (meta) => {
    if (!meta || (typeof meta === "object" && Object.keys(meta).length === 0)) return "—";
    if (typeof meta === "string") return meta;
    try {
        return JSON.stringify(meta);
    } catch {
        return String(meta);
    }
};

const LogsPage = () => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.token);
    const loading = useSelector((state) => state.loading);

    const [rawRows, setRawRows] = useState([]);
    const [serverTotal, setServerTotal] = useState(0);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const [sortState, setSortState] = useState({
        field: "createdAt",
        order: "descend",
    });

    const [filters, setFilters] = useState({
        qAction: "",
        qOperationId: "",
        qAdminUserId: "",
        range: null,
        quick: "all",
    });

    const [showAdvanced, setShowAdvanced] = useState(false);

    const [selectedLog, setSelectedLog] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);

    const openLogDetail = (record) => {
        setSelectedLog(record);
        setDetailModalOpen(true);
    };

    const closeLogDetail = () => {
        setDetailModalOpen(false);
        setSelectedLog(null);
    };

    useEffect(() => {
        if (!token) return;

        const fetchLogs = async () => {
            dispatch(setLoading(true));
            try {
                const res = await getRequest("/api/admin/logs/", token);

                if (!res?.success) {
                    notification.error({
                        message: res?.error?.message || "Ошибка",
                        description: (res?.error?.description || []).join("\n"),
                    });
                    setRawRows([]);
                    setServerTotal(0);
                    return;
                }

                const payload = res.data || {};
                const rows = Array.isArray(payload.data) ? payload.data : [];

                setServerTotal(Number(payload.count) || rows.length);
                setRawRows(
                    rows.map((item) => ({
                        operationId: item.operationId,
                        title: item.title,
                        adminUserId: item.adminUserId,
                        action: item.action,
                        meta: item.meta,
                        createdAt: item.createdAt,
                        updatedAt: item.updatedAt,
                    }))
                );
            } finally {
                dispatch(setLoading(false));
            }
        };

        fetchLogs();
    }, [token, dispatch]);

    const normalized = useMemo(() => {
        return (Array.isArray(rawRows) ? rawRows : []).map((r) => {
            const created = dayjs(r?.createdAt);
            return {
                key: String(r?.operationId ?? `${r?.createdAt ?? ""}-${Math.random()}`),
                operationId: r?.operationId,
                action: safeStr(r?.action).trim(),
                title: safeStr(r?.title).trim(),
                adminUserId: safeStr(r?.adminUserId).trim(),
                meta: r?.meta,
                createdAt: r?.createdAt,
                createdAtTs: created.isValid() ? created.valueOf() : 0,
                createdAtDay: created.isValid() ? created.format("YYYY-MM-DD") : "",
                updatedAt: r?.updatedAt,
            };
        });
    }, [rawRows]);

    const actionOptions = useMemo(() => {
        const map = new Map();
        normalized.forEach((r) => {
            const a = safeStr(r.action).trim();
            if (!a) return;
            map.set(a, (map.get(a) || 0) + 1);
        });
        return [...map.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([value, count]) => ({ value, count }));
    }, [normalized]);

    const applyQuickRange = (quick) => {
        const now = dayjs();
        if (quick === "all") return null;
        if (quick === "24h") return [now.subtract(24, "hour"), now];
        if (quick === "7d") return [now.subtract(7, "day"), now];
        if (quick === "30d") return [now.subtract(30, "day"), now];
        return null;
    };

    const effectiveRange = useMemo(() => {
        if (filters.range && Array.isArray(filters.range) && filters.range.length === 2) return filters.range;
        const r = applyQuickRange(filters.quick);
        return r;
    }, [filters.range, filters.quick]);

    const filtered = useMemo(() => {
        const qAction = safeStr(filters.qAction).trim();
        const qOperationId = safeStr(filters.qOperationId).trim().toLowerCase();
        const qAdminUserId = safeStr(filters.qAdminUserId).trim().toLowerCase();

        const range = effectiveRange;
        const from = range?.[0] ? dayjs(range[0]) : null;
        const to = range?.[1] ? dayjs(range[1]) : null;
        const hasRange = !!(from?.isValid() && to?.isValid());

        return normalized.filter((r) => {
            if (qAction) {
                if (r.action !== qAction) return false;
            }

            if (qOperationId) {
                const id = safeStr(r.operationId).toLowerCase();
                if (!id.includes(qOperationId)) return false;
            }

            if (qAdminUserId) {
                const au = safeStr(r.adminUserId).toLowerCase();
                if (!au.includes(qAdminUserId)) return false;
            }

            if (hasRange) {
                const ts = safeNum(r.createdAtTs);
                if (!ts) return false;
                if (ts < from.valueOf() || ts > to.valueOf()) return false;
            }

            return true;
        });
    }, [normalized, filters.qAction, filters.qOperationId, filters.qAdminUserId, effectiveRange]);

    const summary = useMemo(() => {
        const totalLoaded = normalized.length;
        const totalFiltered = filtered.length;

        const uniqAdmins = new Set();
        const uniqActions = new Set();
        const byAction = new Map();
        const byAdmin = new Map();

        filtered.forEach((r) => {
            if (r.adminUserId) uniqAdmins.add(r.adminUserId);
            if (r.action) uniqActions.add(r.action);

            if (r.action) byAction.set(r.action, (byAction.get(r.action) || 0) + 1);
            if (r.adminUserId) byAdmin.set(r.adminUserId, (byAdmin.get(r.adminUserId) || 0) + 1);
        });

        const topAction = [...byAction.entries()].sort((a, b) => b[1] - a[1])[0] || null;
        const topAdmin = [...byAdmin.entries()].sort((a, b) => b[1] - a[1])[0] || null;

        const days = new Array(14).fill(0).map((_, idx) => dayjs().subtract(13 - idx, "day").format("YYYY-MM-DD"));
        const byDay = days.reduce((acc, d) => {
            acc[d] = 0;
            return acc;
        }, {});
        filtered.forEach((r) => {
            const d = r.createdAtDay;
            if (d && byDay[d] !== undefined) byDay[d] += 1;
        });

        const last14 = days.map((d) => ({ day: d, count: byDay[d] }));
        const peak = [...last14].sort((a, b) => b.count - a.count)[0] || { day: "", count: 0 };
        const last7 = last14.slice(-7);
        const last7Sum = last7.reduce((s, x) => s + x.count, 0);
        const avgPerDay7 = last7.length ? Math.round(last7Sum / last7.length) : 0;

        const actionList = [...byAction.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([action, count]) => ({ action, count, pct: totalFiltered ? Math.round((count / totalFiltered) * 100) : 0 }));

        return {
            totalLoaded,
            totalFiltered,
            uniqAdmins: uniqAdmins.size,
            uniqActions: uniqActions.size,
            topAction,
            topAdmin,
            last14,
            peak,
            avgPerDay7,
            actionList,
        };
    }, [normalized, filtered]);

    const columns = useMemo(
        () => [
            {
                title: "ID",
                dataIndex: "operationId",
                key: "operationId",
                width: 90,
                sorter: (a, b) => safeNum(a.operationId) - safeNum(b.operationId),
                sortOrder: sortState.field === "operationId" ? sortState.order : null,
                render: (v) => <Text strong>#{safeStr(v) || "—"}</Text>,
            },
            {
                title: "Действие",
                dataIndex: "action",
                key: "action",
                width: 260,
                ellipsis: true,
                sorter: (a, b) => safeStr(a.action).localeCompare(safeStr(b.action)),
                sortOrder: sortState.field === "action" ? sortState.order : null,
                render: (v) => {
                    const s = safeStr(v).trim();
                    if (!s) return <Text type="secondary">—</Text>;
                    const hit = actionOptions.find((x) => x.value === s);
                    const count = hit?.count ?? null;
                    return (
                        <Space size={8}>
                            <Tooltip title={s}>
                                <Tag color="blue">{getActionLabel(s)}</Tag>
                            </Tooltip>
                            {count !== null ? <Text type="secondary">{count}</Text> : null}
                        </Space>
                    );
                },
            },
            {
                title: "Заголовок",
                dataIndex: "title",
                key: "title",
                ellipsis: true,
                render: (v) => {
                    const s = safeStr(v).trim();
                    return s ? <Text>{s}</Text> : <Text type="secondary">—</Text>;
                },
            },
            {
                title: "Admin User ID",
                dataIndex: "adminUserId",
                key: "adminUserId",
                width: 240,
                ellipsis: true,
                sorter: (a, b) => safeStr(a.adminUserId).localeCompare(safeStr(b.adminUserId)),
                sortOrder: sortState.field === "adminUserId" ? sortState.order : null,
                render: (v) => {
                    const s = safeStr(v).trim();
                    return s ? (
                        <Space size={8}>
                            <UserOutlined />
                            <Text>{s}</Text>
                        </Space>
                    ) : (
                        <Text type="secondary">—</Text>
                    );
                },
            },
            {
                title: "Meta",
                dataIndex: "meta",
                key: "meta",
                ellipsis: true,
                render: (meta) => {
                    const preview = toJsonPreview(meta);
                    if (preview === "—") return "—";
                    return (
                        <Tooltip title={<Text style={{ whiteSpace: "pre-wrap", color: '#FFF' }}>{preview}</Text>}>
                            <Text style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{preview}</Text>
                        </Tooltip>
                    );
                },
            },
            {
                title: "Дата",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 200,
                sorter: (a, b) => safeNum(a.createdAtTs) - safeNum(b.createdAtTs),
                sortOrder: sortState.field === "createdAt" ? sortState.order : null,
                render: (v) => <Text type="secondary">{formatTs(v)}</Text>,
            },
        ],
        [sortState.field, sortState.order, actionOptions]
    );

    const resetFilters = () => {
        setFilters({ qAction: "", qOperationId: "", qAdminUserId: "", range: null, quick: "all" });
        setPagination((p) => ({ ...p, current: 1 }));
    };

    if (!token) {
        return <Redirect to={routeAuth()} />;
    }

    return (
        <>
            {!loading && (
                <div className="container logs-page">
                    <UserHeader />

                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={14}>
                            <Space direction="vertical" size={4}>
                                <Title level={3} style={{ marginBottom: 0 }}>
                                    Логи
                                </Title>
                                <Text type="secondary">
                                    Поиск по действию, ID, Admin User ID и выборка по датам — плюс быстрые инсайты по активности.
                                </Text>
                            </Space>
                        </Col>
                        <Col xs={24} md={10} style={{ textAlign: "right" }}>
                            <Space wrap>
                                <Button icon={<FilterOutlined />} onClick={() => setShowAdvanced((v) => !v)}>
                                    Фильтры
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
                                    Обновить
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={6}>
                            <Card bordered={false}>
                                <Statistic title="Загружено" value={summary.totalLoaded} />
                                <Text type="secondary">С сервера: {serverTotal}</Text>
                            </Card>
                        </Col>
                        <Col xs={24} md={6}>
                            <Card bordered={false}>
                                <Statistic title="Показано" value={summary.totalFiltered} prefix={<SearchOutlined />} />
                                <Text type="secondary">С учётом фильтров</Text>
                            </Card>
                        </Col>
                        <Col xs={24} md={6}>
                            <Card bordered={false}>
                                <Statistic title="Уникальных действий" value={summary.uniqActions} prefix={<ThunderboltOutlined />} />
                                <Text type="secondary">{summary.topAction ? `Топ: ${getActionLabel(summary.topAction[0])}` : "—"}</Text>
                            </Card>
                        </Col>
                        <Col xs={24} md={6}>
                            <Card bordered={false}>
                                <Statistic title="Уникальных админов" value={summary.uniqAdmins} prefix={<UserOutlined />} />
                                <Text type="secondary">{summary.topAdmin ? `Топ: ${summary.topAdmin[0]}` : "—"}</Text>
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24} md={16}>
                            <Card
                                bordered={false}
                                title="Поиск и даты"
                                extra={
                                    <Space>
                                        <Segmented
                                            value={filters.quick}
                                            onChange={(v) => {
                                                setFilters((p) => ({ ...p, quick: v, range: null }));
                                                setPagination((pg) => ({ ...pg, current: 1 }));
                                            }}
                                            options={[
                                                { label: "Все", value: "all" },
                                                { label: "24ч", value: "24h", icon: <CalendarOutlined /> },
                                                { label: "7д", value: "7d" },
                                                { label: "30д", value: "30d" },
                                            ]}
                                        />
                                        <Button onClick={resetFilters}>Сбросить</Button>
                                    </Space>
                                }
                            >
                                <Row gutter={[12, 12]}>
                                    <Col xs={24} md={8}>
                                        <Select
                                            allowClear
                                            showSearch
                                            style={{ width: "100%" }}
                                            value={filters.qAction || undefined}
                                            onChange={(v) => {
                                                setFilters((p) => ({ ...p, qAction: v || "" }));
                                                setPagination((pg) => ({ ...pg, current: 1 }));
                                            }}
                                            placeholder="Выберите действие"
                                            optionFilterProp="label"
                                            options={[
                                                ...Object.entries(ACTION_LABELS).map(([value, label]) => ({
                                                    value,
                                                    label,
                                                })),
                                            ]}
                                        />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Input
                                            allowClear
                                            value={filters.qOperationId}
                                            onChange={(e) => {
                                                setFilters((p) => ({ ...p, qOperationId: e.target.value }));
                                                setPagination((pg) => ({ ...pg, current: 1 }));
                                            }}
                                            prefix={<SearchOutlined />}
                                            placeholder="Поиск по ID"
                                            inputMode="numeric"
                                        />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Input
                                            allowClear
                                            value={filters.qAdminUserId}
                                            onChange={(e) => {
                                                setFilters((p) => ({ ...p, qAdminUserId: e.target.value }));
                                                setPagination((pg) => ({ ...pg, current: 1 }));
                                            }}
                                            prefix={<SearchOutlined />}
                                            placeholder="Поиск по Admin User ID"
                                        />
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <RangePicker
                                            style={{ width: "100%" }}
                                            showTime
                                            value={filters.range}
                                            onChange={(v) => {
                                                setFilters((p) => ({ ...p, range: v, quick: "all" }));
                                                setPagination((pg) => ({ ...pg, current: 1 }));
                                            }}
                                            allowClear
                                            placeholder={["Дата от", "Дата до"]}
                                        />
                                    </Col>
                                
                                </Row>
                            </Card>
                        </Col>

                        <Col xs={24} md={8}>
                            <Collapse
                                bordered={false}
                                defaultActiveKey={[]}
                                items={[
                                    {
                                        key: "activity",
                                        label: (
                                            <Space>
                                                <BarChartOutlined />
                                                <span>Активность (последние 7 дней)</span>
                                            </Space>
                                        ),
                                        children: summary.last14.length ? (
                                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                                <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                                    <Text type="secondary">Среднее/день</Text>
                                                    <Text strong>{summary.avgPerDay7}</Text>
                                                </Space>
                                                {summary.last14.slice(-7).map((x) => {
                                                    const max = Math.max(1, ...summary.last14.slice(-7).map((t) => t.count));
                                                    const pct = Math.round((x.count / max) * 100);
                                                    const label = dayjs(x.day).format("DD.MM");
                                                    return (
                                                        <Space key={x.day} direction="vertical" size={4} style={{ width: "100%" }}>
                                                            <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                                                <Text type="secondary">{label}</Text>
                                                                <Text strong>{x.count}</Text>
                                                            </Space>
                                                            <Progress percent={pct} showInfo={false} strokeColor={x.count ? "#1677ff" : "#d9d9d9"} />
                                                        </Space>
                                                    );
                                                })}
                                            </Space>
                                        ) : (
                                            <Empty description="Нет данных" />
                                        ),
                                    },
                                ]}
                            />
                        </Col>
                    </Row>

                    {showAdvanced ? (
                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                            <Col xs={24} md={12}>
                                <Card bordered={false} title="Топ действий (в текущей выборке)">
                                    {summary.actionList.length ? (
                                        <Table
                                            size="small"
                                            pagination={false}
                                            rowKey={(r) => r.action}
                                            dataSource={summary.actionList}
                                            columns={[
                                                {
                                                    title: "Действие",
                                                    dataIndex: "action",
                                                    key: "action",
                                                    render: (v) => (
                                                        <Tooltip title={v}>
                                                            <Tag color="blue">{getActionLabel(v)}</Tag>
                                                        </Tooltip>
                                                    ),
                                                },
                                                {
                                                    title: "Кол-во",
                                                    dataIndex: "count",
                                                    key: "count",
                                                    width: 90,
                                                    render: (v) => <Text strong>{v}</Text>,
                                                },
                                                {
                                                    title: "Доля",
                                                    dataIndex: "pct",
                                                    key: "pct",
                                                    width: 90,
                                                    render: (v) => <Text type="secondary">{v}%</Text>,
                                                },
                                            ]}
                                        />
                                    ) : (
                                        <Empty description="Нет данных" />
                                    )}
                                </Card>
                            </Col>

                            <Col xs={24} md={12}>
                                <Card bordered={false} title="Быстрые подсказки">
                                    <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                        <Alert
                                            type="success"
                                            showIcon
                                            message="Лайфхак"
                                            description="Если вводишь несколько критериев — сначала сузь по датам, потом по действию: так быстрее находить нужные события."
                                        />
                                        <Alert
                                            type="warning"
                                            showIcon
                                            message="Проверка"
                                            description={
                                                summary.topAction
                                                    ? `Чаще всего встречается действие: ${getActionLabel(summary.topAction[0])} (${summary.topAction[1]})`
                                                    : "Пока нечего анализировать — выбери период с данными."
                                            }
                                        />
                                    </Space>
                                </Card>
                            </Col>
                        </Row>
                    ) : null}

                    <Divider />

                    <Card
                        bordered={false}
                        title="Таблица логов"
                        extra={
                            <Space>
                                <Tag color="blue">Записей: {filtered.length}</Tag>
                                {effectiveRange?.[0] && effectiveRange?.[1] ? (
                                    <Tag>
                                        {dayjs(effectiveRange[0]).format("DD.MM.YYYY HH:mm")} – {dayjs(effectiveRange[1]).format("DD.MM.YYYY HH:mm")}
                                    </Tag>
                                ) : null}
                            </Space>
                        }
                    >
                        <Table
                            columns={columns}
                            dataSource={filtered}
                            onRow={(record) => ({
                                onClick: () => openLogDetail(record),
                                style: { cursor: "pointer" },
                            })}
                            onChange={(nextPagination, _filters, sorter) => {
                                setPagination({
                                    current: nextPagination.current,
                                    pageSize: nextPagination.pageSize,
                                });

                                const s = Array.isArray(sorter) ? sorter[0] : sorter;
                                if (s?.field && s?.order) {
                                    setSortState({ field: s.field, order: s.order });
                                } else if (s?.field && !s?.order) {
                                    setSortState((prev) => ({ ...prev, field: s.field, order: null }));
                                }
                            }}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: filtered.length,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "20", "50", "100"],
                                showTotal: (t) => `Всего: ${t}`,
                            }}
                        />
                    </Card>
                </div>
            )}

            <Modal
                open={detailModalOpen}
                title={selectedLog ? `Детали лога #${selectedLog.operationId}` : "Детали лога"}
                onCancel={closeLogDetail}
                footer={[
                    <Button key="close" onClick={closeLogDetail}>
                        Закрыть
                    </Button>,
                ]}
                width={700}
                destroyOnClose
            >
                {selectedLog && (
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="ID операции">
                            <Text strong>#{selectedLog.operationId}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Действие">
                            <Space>
                                <Tag color="blue">{getActionLabel(selectedLog.action)}</Tag>
                                <Text type="secondary">({selectedLog.action})</Text>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Заголовок">
                            {selectedLog.title || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Admin User ID">
                            {selectedLog.adminUserId || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата создания">
                            {formatTs(selectedLog.createdAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата обновления">
                            {formatTs(selectedLog.updatedAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Meta (JSON)">
                            {selectedLog.meta && Object.keys(selectedLog.meta).length > 0 ? (
                                <pre style={{ 
                                    margin: 0, 
                                    maxHeight: 300, 
                                    overflow: "auto", 
                                    background: "#f5f5f5", 
                                    padding: 12, 
                                    borderRadius: 6,
                                    fontSize: 12 
                                }}>
                                    {JSON.stringify(selectedLog.meta, null, 2)}
                                </pre>
                            ) : (
                                "—"
                            )}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </>
    );
};

export { routeLogs };

export default LogsPage;